import React, { useEffect } from 'react';
import {
  Button,
  Group,
  MultiSelect,
  Select,
  Stack,
  TextInput,
} from '@mantine/core';
import { RemitSetting } from '../../models/remit-setting.ts';
import { useForm } from '@mantine/form';
import { SiteProvider, Store } from '../../models/siteProvider.ts';
import { RemitSettingType } from '../../models/remit-setting-type.ts';
import { RemittanceFeeMode } from '../../models/remittance-fee-mode.ts';
import useCreateRemitSetting from '../../hooks/useCreateRemitSetting.ts';
import { notifications } from '@mantine/notifications';
import useUpdateRemitSetting from '../../hooks/useUpdateRemitSetting.ts';

type RemitSettingModalProps = {
  siteProvider: SiteProvider;
  stores: Store[];
  remitSetting?: RemitSetting;
  hasDefault: boolean;
  onSave: () => void;
  onCancel: () => void;
};

export default function RemitSettingModal({
  siteProvider,
  stores,
  remitSetting,
  hasDefault = false,
  onSave,
  onCancel,
}: RemitSettingModalProps) {
  const { mutate: createRemitSetting, isPending: isCreateLoading } =
    useCreateRemitSetting();
  const { mutate: updateRemitSetting, isPending: isUpdateLoading } =
    useUpdateRemitSetting();

  const form = useForm<RemitSetting>({
    mode: 'uncontrolled',
    initialValues: {
      siteProviderId: siteProvider.id,
      type: hasDefault ? RemitSettingType.SPECIFIC : RemitSettingType.DEFAULT,
      specifyParkingLotIds: [],
      remittanceFeeMode: RemittanceFeeMode.BEN,
      bankAccount: {
        bankName: '',
        bankBranchName: '',
        fullBankCode: '',
        accountNumber: '',
        accountName: '',
      },
    },
  });

  useEffect(() => {
    if (remitSetting !== null) {
      form.setValues({
        id: remitSetting.id,
        siteProviderId: siteProvider.id,
        type: remitSetting?.type,
        specifyParkingLotIds: remitSetting.specifyParkingLotIds,
        remittanceFeeMode: remitSetting.remittanceFeeMode,
        bankAccount: {
          bankName: remitSetting.bankAccount.bankName,
          bankBranchName: remitSetting.bankAccount.bankBranchName,
          fullBankCode: remitSetting.bankAccount.fullBankCode,
          accountNumber: remitSetting.bankAccount.accountNumber,
          accountName: remitSetting.bankAccount.accountName,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remitSetting, siteProvider]);

  const handleSubmit = (values: RemitSetting) => {
    const mutation =
      remitSetting === null ? createRemitSetting : updateRemitSetting;
    mutation(values, {
      onSuccess: () => {
        onSave();
        notifications.show({
          title: '匯款設定儲存成功',
          message: '您的匯款設定已成功儲存。',
          color: 'green',
        });
      },
    });
  };

  return (
    <Stack>
      <form
        onSubmit={form.onSubmit((values) => {
          handleSubmit(values);
        })}
      >
        <Select
          label="匯款帳戶類型"
          placeholder="選擇匯款帳戶類型"
          data={[
            { value: 'DEFAULT', label: '業者預設' },
            { value: 'SPECIFIC', label: '指定停車場' },
          ]}
          key={form.key('type')}
          {...form.getInputProps('type')}
          disabled
          required
        />
        {form.getValues().type === RemitSettingType.SPECIFIC && (
          <MultiSelect
            label="指定停車場"
            placeholder="選擇停車場"
            data={stores?.map((store) => ({
              value: store.id,
              label: store.name,
            }))}
            key={form.key('specifyParkingLotIds')}
            {...form.getInputProps('specifyParkingLotIds')}
            required
            clearable
            searchable
          />
        )}
        <TextInput
          label="銀行"
          placeholder="輸入銀行名稱"
          key={form.key('bankAccount.bankName')}
          {...form.getInputProps('bankAccount.bankName')}
          required
        />
        <TextInput
          label="分行"
          placeholder="輸入分行"
          key={form.key('bankAccount.bankBranchName')}
          {...form.getInputProps('bankAccount.bankBranchName')}
          required
        />
        <TextInput
          label="銀行代碼"
          placeholder="銀行代碼"
          key={form.key('bankAccount.ba')}
          {...form.getInputProps('bankAccount.fullBankCode')}
          required
        />
        <TextInput
          label="帳號"
          placeholder="輸入帳號"
          key={form.key('bankAccount.accountNumber')}
          {...form.getInputProps('bankAccount.accountNumber')}
          required
        />
        <TextInput
          label="戶名"
          placeholder="輸入戶名"
          key={form.key('bankAccount.accountName')}
          {...form.getInputProps('bankAccount.accountName')}
          required
        />
        <Select
          label="手續費模式"
          data={[
            { value: 'OUR', label: '不內扣' },
            { value: 'BEN', label: '內扣' },
          ]}
          key={form.key('remittanceFeeMode')}
          {...form.getInputProps('remittanceFeeMode')}
          required
        />
        <Group mt="md">
          <Button variant="outline" onClick={onCancel}>
            取消
          </Button>
          <Button type="submit" loading={isCreateLoading || isUpdateLoading}>
            {remitSetting === null ? '儲存' : '更新'}
          </Button>
        </Group>
      </form>
    </Stack>
  );
}
